import { Suspense } from 'react';

// ES 2015
import { faInfoCircle } from '@fortawesome/pro-light-svg-icons';
import BaseStyling from 'config/baseStyling';
import i18n from 'config/i18n';
import { setupAxios } from 'config/main';
import { MuiTheme } from 'config/materialUI';
import createConfiguredStore from 'config/reduxStore';
import 'dayjs/locale/nl';
import 'overlayscrollbars/css/OverlayScrollbars.css';
import { createRoot, Root } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Provider } from 'react-redux';
import { setOrganisationPreferences } from 'redux/actionCreators';

import { TENANT } from 'constants/misc';

import { getDateFnsLocale, getLocale } from 'utils/date';
import { setStore } from 'utils/reduxStore';

import { ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns';
import { LicenseInfo } from '@mui/x-license-pro';

import HourManagement from 'components/admin-portal/HourManagement';
import Realization from 'components/admin-portal/Procedures/Realization';
import AdminSettings from 'components/admin-portal/Settings/Settings';
import DataQueue from 'components/admin-portal/queues/DataQueue';
import ImportQueue from 'components/admin-portal/queues/ImportQueue';
import QueueTabs from 'components/admin-portal/queues/QueueTabs';
import ReportQueue from 'components/admin-portal/queues/ReportQueue';
import SchedulingQueue from 'components/admin-portal/queues/SchedulingQueue';
import CostCalculationExternal from 'components/employee-card/cost-calculation-external/CostCalculationExternal';
import PlanUnitDistribution from 'components/employee-card/employment/planUnitDistribution/PlanUnitDistribution';
import EmployeeQualifications from 'components/employee-card/preferences/EmployeeQualifications/EmployeeQualifications';
import RegionalPoolEmploymentOrganisations from 'components/employee-card/preferences/RegionalPoolEmploymentOrganisations/RegionalPoolEmploymentOrganisations';
import TeamrosterEmployeesHeader from 'components/employee-portal/teamroster/RosterViewByEmployee/HeaderDojoWrapper';
import Teamroster from 'components/employee-portal/teamroster/Teamroster';
import WorkPatternPage from 'components/employee-portal/work-pattern/WorkPatternPage';
import FlexHistory from 'components/flex-portal/FlexHistory/FlexHistory';
import FlexAssignments from 'components/flex-portal/Assignments/FlexAssignments';
import RosterAssignments from 'components/roster-portal/Assignments/RosterAssignments';
import CreateEmployeeDialog from 'components/flex-portal/CreateEmployeeDialog/CreateEmployeeDialog';
import FlexMatchSettingsDialog from 'components/flex-portal/match/FlexMatchSettingsDialog/FlexMatchSettingsDialog';
import Administrator from 'components/roster-portal/Administrator/Administrator';
import RosterHome from 'components/roster-portal/home/Home';
import OpenSelfMatchDialog from 'components/roster-portal/roster/open-requests/OpenSelfMatchDialog/OpenSelfMatchDialog';
import {
  Autocomplete,
  AutocompleteWithPreference,
  ColorPickerPopover,
  CreateDutyForAbsenceDialog,
  DeleteAbsenceDialog,
  FontAwesomeSvgIcon,
  MultiSelect,
  Select,
  showSnackbar
} from 'components/shared';
import BalanceBudget from 'components/shared/BalanceBudget/BalanceBudget';
import { CostCalculationExternalSettings } from 'components/shared/CostCalculationExternalSurcharge/CostCalculationExternalSettings';
import DutyCodesGrid from 'components/shared/DutyCodesGrid/DutyCodesGrid';
import EmployeeSelector from 'components/shared/EmployeeSelector';
import PlanGroupsDialog from 'components/shared/PlanGroups/PlanGroupsDialog';
import PlanUnitMultiSelect from 'components/shared/PlanUnitMultiSelect';
import PlanUnitSelector from 'components/shared/PlanUnitSelector';
import PreferredEmployees from 'components/shared/PreferredEmployees';
import PreferredEmployeesDialog from 'components/shared/PreferredEmployeesDialog';
import ShareCalendarDialog from 'components/shared/ShareCalendarDialog';
import GeneratedReports from 'components/shared/reports/GeneratedReports';

import BaseTabsRouting from './components/admin-portal/BaseTabsRouting/BaseTabsRouting';
import {
  grantsHeaders,
  grantsViews
} from './components/admin-portal/Grants/tabsConfig';
import {
  procedureHeaders,
  procedureTableViews
} from './components/admin-portal/Procedures/tabsConfig';
import {
  settingsHeaders,
  settingsViews
} from './components/admin-portal/Settings/tabsConfig';
import AuxiliaryValues from './components/admin-portal/Tables/AuxiliaryValues/AuxiliaryValues';
import MaxCLASettings from './components/admin-portal/Tables/CLA/MaxCLASettings/MaxCLASettings';
import PremiumPay from './components/admin-portal/Tables/CLA/PremiumPay';
import SalaryScales from './components/admin-portal/Tables/CLA/SalaryScales/SalaryScales';
import DHTClaSettings from './components/admin-portal/Tables/Settings/DHTClaSettings/DHTClaSettings';
import Holidays from './components/admin-portal/Tables/Year/Holidays/Holidays';
import {
  adminTableHeaders,
  adminTableViews
} from './components/admin-portal/Tables/tabsConfig';
import TravelDistances from './components/admin-portal/TravelDistances';
import AdditionalPropertiesGrid from './components/employee-card/employment/additionalProperties/AdditionalPropertiesGrid';
import TravelDistancesOverview from './components/employee-card/employment/travel-distances-overview/TravelDistancesOverview';
import RegionalPoolRequests from './components/regional-pool-portal/RegionalPoolRequests';
import ActivityPlanning from './components/roster-portal/activity-planning/ActivityPlanning';
import CapacityPlan from './components/roster-portal/capacity/CapacityPlan';
import ConnectSelfMatchDutiesDialog from './components/roster-portal/roster/open-requests/ConnectSelfMatchDutiesDialog/ConnectSelfMatchDutiesDialog';
import AbsenceNotices from './components/shared/AbsenceNotices/AbsenceNotices';

import { BalanceBudgetGiftProcedure } from 'components/admin-portal/Procedures/LeaveProcedures/components/BalanceBudgetGiftProcedure';
import { TravelExpenseClaims } from 'components/roster-portal/home/travel-expense-claims';

import './config/App.scss';
import './config/i18n';
import EmployeeMessages from 'components/employee-portal/messages/Messages';
import EmployeeNotes from 'components/employee-portal/notes/EmployeeNotes';
import EmployeeLeaveRequests from 'components/employee-portal/leave-requests/EmployeeLeaveRequests';
import Preferences from 'components/roster-portal/plan-units/Preferences/Preferences';
import Clusters from 'components/roster-portal/plan-units/Clusters/Clusters';
import FormationAndSortorder from 'components/roster-portal/plan-units/FormationAndSortorder/FormationAndSortorder';
import LeaveRequests from 'components/employee-portal/leave-requests/LeaveRequests';
import Settings from 'components/roster-portal/plan-units/Settings/Settings';
import TravelMovements from 'components/employee-card/employment/travel-movements/TravelMovements';

const WEBPACK_PUBLIC_PATH = process.env.REACT_APP_WEBPACK_PUBLIC_PATH || '/';
const isProd = process.env.NODE_ENV === 'production';

__webpack_public_path__ = (isProd ? '/' : WEBPACK_PUBLIC_PATH + '/').replace(
  '#{tenantName}',
  TENANT
);

LicenseInfo.setLicenseKey(
  '8cca761874110349acf24c7fc602be29T1JERVI6NDM1MzQsRVhQSVJZPTE2ODM5MDIxNjcwMDAsS0VZVkVSU0lPTj0x'
);

setupAxios();

const createNewStore = () => {
  const store = createConfiguredStore();
  window.reduxStore = store;
  return store;
};

let store = createNewStore();
setStore(store);

type ReactRoots = Record<string, Root>;
const reactRoots: ReactRoots = {};

const unmountComponentAtRootId = (rootId) => {
  const root = reactRoots[rootId];

  if (root) {
    root.unmount();
  }

  delete reactRoots[rootId];
};

window._unmountComponentAtRootId = unmountComponentAtRootId;

window.addEventListener('userLoggedOut', () => {
  for (const key of Object.keys(reactRoots)) {
    if (reactRoots[key]) {
      unmountComponentAtRootId(key);
      delete reactRoots[key];
    }
  }

  const { organisationPreferences } = store.getState();
  store = createNewStore();
  store.dispatch(setOrganisationPreferences(organisationPreferences));
  setStore(store);
});

const t = i18n.getFixedT(getLocale(), 'common');
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
      cacheTime: 0,
      onError: () => {
        showSnackbar({
          message: t('somethingWentWrong'),
          severity: 'error'
        });
      }
    }
  }
});

// TODO: After migration is done, move Suspense to ROOT node, to handle translation fallback
window._renderComponent = (
  componentName: string,
  domNode: HTMLElement,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  props: any = {},
  rootId: string
) => {
  if (!domNode) {
    domNode = document.createElement('div');
    document.body.appendChild(domNode);
  }

  let root;

  if (reactRoots[rootId]) {
    root = reactRoots[rootId];
  } else {
    root = createRoot(domNode);
    reactRoots[rootId] = root;
  }

  const renderComponent = () => {
    root.render(
      <LocalizationProvider
        dateAdapter={AdapterDateFns}
        adapterLocale={getDateFnsLocale()}
      >
        <QueryClientProvider client={queryClient}>
          <Provider store={store}>
            <ThemeProvider theme={MuiTheme}>
              <BaseStyling>
                <Suspense fallback={<></>}>
                  {(function RenderComponent() {
                    switch (componentName) {
                      case 'GeneratedReports':
                        return <GeneratedReports />;
                      case 'DutyCodesGrid':
                        return <DutyCodesGrid {...props} />;
                      case 'Preferences':
                        return <Preferences {...props} />;
                      case 'Settings':
                        return <Settings {...props} />;
                      case 'FormationAndSortorder':
                        return <FormationAndSortorder {...props} />;
                      case 'Clusters':
                        return <Clusters {...props} />;
                      case 'RosterHome':
                        return <RosterHome />;
                      case 'Teamroster':
                        return <Teamroster />;
                      case 'LeaveRequests':
                        return <LeaveRequests {...props} />;
                      case 'OpenSelfMatchDialog':
                        return (
                          <OpenSelfMatchDialog
                            {...props}
                            onHidden={() => unmountComponentAtRootId(rootId)}
                          />
                        );
                      case 'ConnectSelfMatchDutiesDialog':
                        return (
                          <ConnectSelfMatchDutiesDialog
                            {...props}
                            onHidden={() => unmountComponentAtRootId(rootId)}
                          />
                        );
                      case 'FlexMatchSettingsDialog':
                        return (
                          <FlexMatchSettingsDialog
                            {...props}
                            onHidden={() => unmountComponentAtRootId(rootId)}
                          />
                        );
                      case 'PlanGroupsDialog':
                        return (
                          <PlanGroupsDialog
                            onHidden={() => unmountComponentAtRootId(rootId)}
                          />
                        );
                      case 'MultiSelect':
                        return <MultiSelect {...props} />;
                      case 'PlanUnitMultiSelect':
                        return <PlanUnitMultiSelect {...props} />;
                      case 'PlanUnitSelector':
                        return <PlanUnitSelector {...props} />;
                      case 'Select':
                        return <Select {...props} />;
                      case 'Autocomplete':
                        return <Autocomplete {...props} />;
                      case 'AutocompleteWithPreference':
                        return <AutocompleteWithPreference {...props} />;
                      case 'EmployeeSelector':
                        return <EmployeeSelector {...props} />;
                      case 'EmployeeQualifications':
                        return <EmployeeQualifications {...props} />;
                      case 'PreferredEmployees':
                        return <PreferredEmployees {...props} />;
                      case 'PreferredEmployeesDialog':
                        return (
                          <PreferredEmployeesDialog
                            {...props}
                            onHidden={() => unmountComponentAtRootId(rootId)}
                          />
                        );
                      case 'RegionalPoolEmploymentOrganisations':
                        return (
                          <RegionalPoolEmploymentOrganisations {...props} />
                        );
                      case 'WorkPatternPage':
                        return <WorkPatternPage {...props} />;
                      case 'EmployeeLeaveRequests':
                        return <EmployeeLeaveRequests {...props} />;
                      case 'BalanceBudget':
                        return <BalanceBudget {...props} />;
                      case 'EmployeeMessages':
                        return <EmployeeMessages {...props} />;
                      case 'EmployeeNotes':
                        return <EmployeeNotes {...props} />;
                      case 'TeamrosterEmployeesHeader':
                        return <TeamrosterEmployeesHeader {...props} />;
                      case 'AdminSettings':
                        return <AdminSettings {...props} />;
                      case 'CreateEmployeeDialog':
                        return (
                          <CreateEmployeeDialog
                            {...props}
                            onHidden={() => unmountComponentAtRootId(rootId)}
                          />
                        );
                      case 'CapacityPlan':
                        return (
                          <CapacityPlan
                            {...props}
                            onHidden={() => unmountComponentAtRootId(rootId)}
                          />
                        );
                      case 'PlanUnitDistribution':
                        return <PlanUnitDistribution {...props} />;
                      case 'ShareCalendarDialog':
                        return <ShareCalendarDialog />;
                      case 'RosterAssignments':
                        return <RosterAssignments />;
                      case 'FlexHistory':
                        return <FlexHistory />;
                      case 'FlexAssignments':
                        return <FlexAssignments />;
                      case 'QueueTabs':
                        return (
                          <QueueTabs
                            {...props}
                            onHidden={() => unmountComponentAtRootId(rootId)}
                          />
                        );
                      case 'ImportQueue':
                        return <ImportQueue />;
                      case 'DataQueue':
                        return <DataQueue />;
                      case 'ReportQueue':
                        return <ReportQueue />;
                      case 'SchdulingQueue':
                        return <SchedulingQueue />;
                      case 'HourManagement':
                        return <HourManagement />;
                      case 'Realization':
                        return <Realization />;
                      case 'ColorPickerPopover':
                        return <ColorPickerPopover {...props} />;
                      case 'DeleteAbsenceDialog':
                        return <DeleteAbsenceDialog {...props} />;
                      case 'CreateDutyForAbsenceDialog':
                        return <CreateDutyForAbsenceDialog {...props} />;
                      case 'Snackbar':
                        if (props.icon) {
                          if (props.icon === 'info') {
                            props.icon = (
                              <FontAwesomeSvgIcon
                                icon={faInfoCircle}
                                color="primary"
                              />
                            );
                          } else {
                            delete props.icon;
                          }
                        }
                        showSnackbar({ ...props });
                        break;
                      case 'ActivityPlanning':
                        return <ActivityPlanning {...props} />;
                      case 'TravelDistances':
                        return <TravelDistances />;
                      case 'TravelDistancesOverview':
                        return <TravelDistancesOverview {...props} />;
                      case 'RegionalPoolRequests':
                        return <RegionalPoolRequests {...props} />;
                      case 'DHTClaSettings':
                        return <DHTClaSettings />;
                      case 'AbsenceNotices':
                        return <AbsenceNotices />;
                      case 'CostCalculationExternalSettings':
                        return <CostCalculationExternalSettings {...props} />;
                      case 'CostCalculationExternal':
                        return <CostCalculationExternal {...props} />;
                      case 'AdditionalPropertiesGrid':
                        return <AdditionalPropertiesGrid {...props} />;
                      case 'SalaryScales':
                        return <SalaryScales />;
                      case 'RosterAdministrator':
                        return <Administrator {...props} />;
                      case 'AdminTables':
                        return (
                          <BaseTabsRouting
                            {...props}
                            headers={adminTableHeaders}
                            views={adminTableViews}
                            userGrantNeeded="hasAccessToAdminTablesReact"
                          />
                        );
                      case 'AdminProcedures':
                        return (
                          <BaseTabsRouting
                            {...props}
                            headers={procedureHeaders}
                            views={procedureTableViews}
                            userGrantNeeded="hasAccessToAdminProceduresReact"
                          />
                        );
                      case 'AdminSettingsReact':
                        return (
                          <BaseTabsRouting
                            {...props}
                            headers={settingsHeaders}
                            views={settingsViews}
                            userGrantNeeded="hasAccessToAdminSettingsReact"
                          />
                        );
                      case 'AdminGrants':
                        return (
                          <BaseTabsRouting
                            {...props}
                            headers={grantsHeaders}
                            views={grantsViews}
                            userGrantNeeded="hasAccessToAdminGrantsReact"
                          />
                        );
                      case 'PremiumPay':
                        return <PremiumPay {...props} />;
                      case 'Holidays':
                        return <Holidays {...props} />;
                      case 'MaxBuh':
                        return <MaxCLASettings {...props} />;
                      case 'AuxiliaryValues':
                        return <AuxiliaryValues {...props} />;
                      case 'BalanceBudgetGiftProcedure':
                        return <BalanceBudgetGiftProcedure />;
                      case 'TravelExpenseClaims':
                        return (
                          <TravelExpenseClaims
                            disableActions={true}
                            {...props}
                          />
                        );
                      case 'TravelMovements':
                        return <TravelMovements {...props} />;
                      default:
                        break;
                    }
                  })()}
                </Suspense>
              </BaseStyling>
            </ThemeProvider>
          </Provider>
        </QueryClientProvider>
      </LocalizationProvider>
    );
  };

  renderComponent();

  if (module.hot) {
    switch (componentName) {
      case 'GeneratedReports':
        module.hot.accept(
          'components/shared/reports/GeneratedReports',
          renderComponent
        );
        break;
      case 'DutyCodesGrid':
        module.hot.accept(
          'components/shared/DutyCodesGrid/DutyCodesGrid',
          renderComponent
        );
        break;
      case 'RosterHome':
        module.hot.accept(
          'components/roster-portal/home/Home',
          renderComponent
        );
        break;
      case 'Teamroster':
        module.hot.accept(
          'components/employee-portal/teamroster/Teamroster',
          renderComponent
        );
        break;
      case 'OpenSelfMatchDialog':
        module.hot.accept(
          'components/roster-portal/roster/open-requests/OpenSelfMatchDialog/OpenSelfMatchDialog',
          renderComponent
        );
        break;
      case 'ConnectSelfMatchDutiesDialog':
        module.hot.accept(
          'components/roster-portal/roster/open-requests/ConnectSelfMatchDutiesDialog/ConnectSelfMatchDutiesDialog',
          renderComponent
        );
        break;
      case 'FlexMatchSettingsDialog':
        module.hot.accept(
          'components/flex-portal/match/FlexMatchSettingsDialog/FlexMatchSettingsDialog',
          renderComponent
        );
        break;
      case 'PlanGroupsDialog':
        module.hot.accept(
          'components/shared/PlanGroups/PlanGroupsDialog',
          renderComponent
        );
        break;
      case 'MultiSelect':
        module.hot.accept('components/shared/MultiSelect', renderComponent);
        break;
      case 'PlanUnitMultiSelect':
        module.hot.accept(
          'components/shared/PlanUnitMultiSelect',
          renderComponent
        );
        break;
      case 'PlanUnitSelector':
        module.hot.accept(
          'components/shared/PlanUnitSelector',
          renderComponent
        );
        break;
      case 'Select':
        module.hot.accept('components/shared/Select', renderComponent);
        break;
      case 'Autocomplete':
        module.hot.accept(
          'components/shared/Autocomplete/Autocomplete',
          renderComponent
        );
        break;
      case 'AutocompleteWithPreference':
        module.hot.accept(
          'components/shared/Autocomplete/AutocompleteWithPreference',
          renderComponent
        );
        break;
      case 'EmployeeSelector':
        module.hot.accept(
          'components/shared/EmployeeSelector',
          renderComponent
        );
        break;
      case 'EmployeeQualifications':
        module.hot.accept(
          'components/employee-card/preferences/EmployeeQualifications/EmployeeQualifications',
          renderComponent
        );
        break;
      case 'PlanningCycleDialog':
        module.hot.accept(
          'components/shared/PlanningCycleDialog',
          renderComponent
        );
        break;
      case 'PreferredEmployees':
        module.hot.accept(
          'components/shared/PreferredEmployees',
          renderComponent
        );
        break;
      case 'PreferredEmployeesDialog':
        module.hot.accept(
          'components/shared/PreferredEmployeesDialog',
          renderComponent
        );
        break;
      case 'BalanceBudget':
        module.hot.accept(
          'components/shared/BalanceBudget/BalanceBudget',
          renderComponent
        );
        break;
      case 'AdminSettings':
        module.hot.accept(
          'components/admin-portal/Settings/Settings',
          renderComponent
        );
        break;
      case 'CreateEmployeeDialog':
        module.hot.accept(
          'components/flex-portal/CreateEmployeeDialog/CreateEmployeeDialog',
          renderComponent
        );
        break;
      case 'CapacityPlan':
        module.hot.accept(
          'components/roster-portal/capacity/CapacityPlan',
          renderComponent
        );
        break;
      case 'PlanUnitDistribution':
        module.hot.accept(
          'components/employee-card/employment/planUnitDistribution/PlanUnitDistribution',
          renderComponent
        );
        break;
      case 'ShareCalendarDialog':
        module.hot.accept(
          'components/shared/ShareCalendarDialog/ShareCalendarDialog',
          renderComponent
        );
        break;
      case 'ActivityPlanning':
        module.hot.accept(
          'components/roster-portal/activity-planning/ActivityPlanning',
          renderComponent
        );
        break;
      case 'FlexHistory':
        module.hot.accept(
          'components/flex-portal/FlexHistory/FlexHistory',
          renderComponent
        );
        break;
      case 'FlexAssignments':
        module.hot.accept(
          'components/flex-portal/Assignments/FlexAssignments',
          renderComponent
        );
        break;
      case 'RosterAssignments':
        module.hot.accept(
          'components/roster-portal/Assignments/RosterAssignments',
          renderComponent
        );
        break;
      case 'ImportQueue':
        module.hot.accept(
          'components/admin-portal/queues/ImportQueue/ImportQueue',
          renderComponent
        );
        break;
      case 'QueueTabs':
        module.hot.accept(
          'components/admin-portal/queues/QueueTabs/QueueTabs',
          renderComponent
        );
        break;
      case 'HourManagement':
        module.hot.accept(
          'components/admin-portal/HourManagement/HourManagement',
          renderComponent
        );
        break;
      case 'Realization':
        module.hot.accept(
          'components/admin-portal/Procedures/Realization/Realization',
          renderComponent
        );
        break;
      case 'TravelDistances':
        module.hot.accept(
          'components/admin-portal/TravelDistances',
          renderComponent
        );
        break;
      case 'ColorPickerPopover':
        module.hot.accept(
          'components/shared/ColorPickerPopover/ColorPickerPopover',
          renderComponent
        );
        break;
      case 'DeleteAbsenceDialog':
        module.hot.accept(
          'components/shared/DeleteAbsenceDialog/DeleteAbsenceDialog',
          renderComponent
        );
        break;
      case 'CreateDutyForAbsenceDialog':
        module.hot.accept(
          'components/shared/CreateDutyForAbsenceDialog/CreateDutyForAbsenceDialog',
          renderComponent
        );
        break;
      case 'TravelDistancesOverview':
        module.hot.accept(
          'components/employee-card/employment/travel-distances-overview/TravelDistancesOverview',
          renderComponent
        );
        break;
      case 'RegionalPoolRequests':
        module.hot.accept(
          'components/regional-pool-portal/RegionalPoolRequests',
          renderComponent
        );
        break;
      case 'AbsenceNotices':
        module.hot.accept(
          'components/shared/AbsenceNotices/AbsenceNotices',
          renderComponent
        );
        break;
      case 'CostCalculationExternalSettings':
        module.hot.accept(
          'components/shared/CostCalculationExternalSurcharge/CostCalculationExternalSettings'
        );
        break;
      case 'CostCalculationExternal':
        module.hot.accept(
          'components/employee-card/cost-calculation-external/CostCalculationExternal'
        );
        break;
      case 'AdditionalPropertiesGrid':
        module.hot.accept(
          'components/employee-card/employment/additionalProperties/AdditionalPropertiesGrid'
        );
        break;
      case 'BalanceBudgetGiftProcedure':
        module.hot.accept(
          'components/admin-portal/Procedures/LeaveProcedures/components/BalanceBudgetGiftProcedure'
        );
        break;
      default:
        break;
    }
  }

  return root;
};
